/**
 * Engine HTTP client for contacts. Mirrors the single route mounted in
 * `services/engine/src/api/routes/contacts.ts`:
 *
 *   GET /api/contacts/:contactId → Contact
 *
 * Contacts are cross-pipeline (a single contact can drive leads in multiple
 * pipelines), so this client lives at `lib/api/contacts.ts` rather than under
 * a feature folder. The error class is reused from `pipelines.ts` via the
 * `ApiError` alias — the historical name (`PipelineApiError`) outlives its
 * scope; a future cleanup will rename it across both files.
 */

import { z } from 'zod'
import type { Contact, ContactId } from '@ludus/core'
import { apiClient } from './client.ts'
import { ApiError, classifyApiError } from './pipelines.ts'

const contactSchema = z.object({
  id: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  middleName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  isDeleted: z.boolean(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
})

function castContact(c: z.infer<typeof contactSchema>): Contact {
  return { ...c, id: c.id as ContactId }
}

export async function fetchContact(contactId: ContactId): Promise<Contact> {
  try {
    const { data } = await apiClient.get(`/contacts/${contactId}`)
    return castContact(contactSchema.parse(data))
  } catch (err) {
    throw classifyApiError(err)
  }
}

export { ApiError }
